import {
  MousePointer2,
  Scissors,
  Eraser,
  PaintBucket,
  Move,
  RotateCw,
  Ruler,
  Scaling,
  CircleDashed,
  Compass,
  SlidersHorizontal,
  Hand,
  Sparkles,
  WandSparkles,
  Maximize2,
  PenLine,
  Lock,
  LocateFixed,
  Wrench,
  Shield,
  Eye,
  Upload,
  Save,
  LassoSelect,
  SquareRoundCorner,
  Image,
  Expand,
  Brush,
  TableProperties,
} from 'lucide-react';

// Tool name mappings for readable display names
export const TOOL_NAMES = {
  pointer: 'Pointer',
  // Scan Process - Preparing Scan
  scan_preparing_scissors: 'Clip',
  scan_preparing_eraser: 'Eraser',
  scan_preparing_pencil: 'Fill',
  scan_preparing_move: 'Move Socket',
  scan_preparing_rotate: 'Rotate Socket',
  scan_preparing_measure: 'Measure',
  scan_preparing_circumference: 'Circumference',
  scan_preparing_lasso: 'Lasso Selection',
  scan_preparing_global_smooth: 'Global Smoothing',
  main_rectification_smoothning: 'smoothening',

  // Scan Process - Axes Alignment
  scan_axes_pin: 'Axes',
  scan_axes_movement: 'Alignment',

  // Main Process - Rectification
  main_rectification_measure_reduction: 'Measure Reduction',
  main_rectification_scoop_recharge: 'Carve Relief',
  main_rectification_blocking_zone: 'Blocking Zone',
  main_rectification_global_reduction: 'Global Reduction',
  main_rectification_inflate_deflate: 'Inflate / Deflate',
  main_rectification_sculpt_smooth: 'Sculpt Smooth',
  main_rectification_carve_smoothning: 'Smoothing',
  main_rectification_global_smooth: 'Global Smoothing',
  main_rectification_circumference_lines: 'Circumference Lines',

  // Main Process - Elongation
  main_elongation_elongation: 'Elongation',

  // Main Process - Trimline
  main_trimline_trimline: 'Trimline',

  // Main Process - Flares
  main_flares_flare: 'Flare',
  main_flares_smoothning: 'Flares Smoothening',
  main_flares_global_smooth: 'Global Smoothing',

  // Main Process - ML Reduction
  main_ml_reduction_brush: 'ML reduction',
  main_ml_reduction_smoothning: 'ML Reduction Smoothing',

  // Main Process - Thickness
  main_thickness_thickness: 'Thickness',
  main_thickness_measure: 'Measure',
  main_thickness_adjust: 'Adjust',

  // Main Process - Locking Mechanism
  main_locking_lock: 'Locking System',
  main_locking_position: 'Position',
  main_locking_adjust: 'Adjust',

  // Main Process - Alignment
  main_alignment_socket_pose: 'Align',
  main_alignment_rotate: 'Rotate',
  main_alignment_measure: 'Measure',

  // Main Process - Valve
  main_valve_valve: 'Valve',
  main_valve_position: 'Position',
  main_valve_adjust: 'Adjust',

  // Main Process - Valve pattern
  main_valve_pattern_projection: 'Valve pattern',

  // Main Process - Logo placement
  main_logo_placement: 'Logo',

  // Main Process - Pads
  main_pads_pad: 'Pad',
  main_pads_position: 'Position',
  main_pads_adjust: 'Adjust',


  // Main Process - Finalization
  main_finalization_review: 'Review',
  main_finalization_export: 'Export',
  main_finalization_save: 'Save'
};

// tools.js

/** Sort tool configs that define `order` (lower first). */
export function getToolIdsByOrder(stepToolsConfig) {
  return Object.values(stepToolsConfig)
    .filter((t) => t && typeof t.order === 'number')
    .sort((a, b) => a.order - b.order)
    .map((t) => t.id);
}

export const TOOLS_CONFIG = {
  SCAN_PROCESS: {
    // `group` splits the toolbar buttons into visually separated blocks:
    // mesh edits (`meshEdit`) sit above the socket transforms (`transform`).
    // Grouping is presentation only — `category` still decides where a tool
    // renders (toolbar vs. scene overlay).
    PREPARING_SCAN: {
      TRIM: { id: 'scan_preparing_scissors', name: TOOL_NAMES.scan_preparing_scissors, icon: 'scissors', kind: 'trim', category: 'operation', group: 'meshEdit', order: 1 },
      LASSO: { id: 'scan_preparing_lasso', name: TOOL_NAMES.scan_preparing_lasso, icon: 'lasso', kind: 'lasso', category: 'operation', group: 'meshEdit', order: 2 },
      ERASE: { id: 'scan_preparing_eraser', name: TOOL_NAMES.scan_preparing_eraser, icon: 'eraser', kind: 'erase', category: 'operation', group: 'meshEdit', order: 3 },
      FILL: { id: 'scan_preparing_pencil', name: TOOL_NAMES.scan_preparing_pencil, icon: 'paintBucket', kind: 'fill', category: 'operation', group: 'meshEdit', order: 4 },
      MOVE: { id: 'scan_preparing_move', name: TOOL_NAMES.scan_preparing_move, icon: 'move', kind: 'move', category: 'operation', group: 'transform', order: 5 },
      ROTATE: { id: 'scan_preparing_rotate', name: TOOL_NAMES.scan_preparing_rotate, icon: 'rotateCw', kind: 'rotate', category: 'operation', group: 'transform', order: 6 },
      MEASURE: { id: 'scan_preparing_measure', name: TOOL_NAMES.scan_preparing_measure, icon: 'ruler', kind: 'measure', category: 'measurement', order: 7 },
      CIRCUMFERENCE: { id: 'scan_preparing_circumference', name: TOOL_NAMES.scan_preparing_circumference, icon: 'circleDashed', kind: 'circumference', category: 'measurement', order: 8 },
      // Smoothing moved here from Rectification; the id keeps its legacy
      // `main_rectification_*` prefix so persisted state and help content resolve.
      SMOOTH: { id: 'main_rectification_smoothning', name: 'Smoothing', icon: 'sparkles', kind: 'smooth', category: 'operation', group: 'meshEdit', order: 9 },
      GLOBAL_SMOOTH: { id: 'scan_preparing_global_smooth', name: TOOL_NAMES.scan_preparing_global_smooth, icon: 'wandSparkles', kind: 'globalSmooth', category: 'operation', group: 'meshEdit', order: 10 },
    },
    AXES_ALIGNMENT: {
      AXES: { id: 'scan_axes_pin', name: TOOL_NAMES.scan_axes_pin, icon: 'compass', kind: 'axes', category: 'operation', order: 1 },
      ALINGMENT: { id: 'scan_axes_movement', name: TOOL_NAMES.scan_axes_movement, icon: 'move', kind: 'alignMove', category: 'operation', order: 2 },
    },
  },
  MAIN_PROCESS: {
    RECTIFICATION: {
      // Reduction / relief work first (Inflate / Deflate closes it out, right
      // after Scoop Recharge), then the smoothing brushes as their own group.
      main_rectification_blocking_zone: { id: 'main_rectification_blocking_zone', name: 'Blocking Zone', icon: 'shield', kind: 'blockingZone', category: 'operation', group: 'shaping', order: 1 },
      main_rectification_global_reduction: { id: 'main_rectification_global_reduction', name: 'Global Reduction', icon: 'slidersHorizontal', kind: 'globalReduction', category: 'operation', group: 'shaping', order: 2 },
      main_rectification_measure_reduction: { id: 'main_rectification_measure_reduction', name: 'Measure Reduction', icon: 'scaling', kind: 'measureReduction', category: 'operation', group: 'shaping', order: 3 },
      main_rectification_scoop_recharge: { id: 'main_rectification_scoop_recharge', name: 'Scoop Recharge', icon: 'hand', kind: 'carve', category: 'operation', group: 'shaping', order: 4 },
      main_rectification_inflate_deflate: { id: 'main_rectification_inflate_deflate', name: TOOL_NAMES.main_rectification_inflate_deflate, icon: 'expand', kind: 'inflateDeflate', category: 'operation', group: 'shaping', order: 5 },
      main_rectification_carve_smoothning: { id: 'main_rectification_carve_smoothning', name: 'Smoothing', icon: 'sparkles', kind: 'carveSmooth', category: 'operation', group: 'smoothing', order: 6 },
      // Freehand real-time smoothing brush, as distinct from the zone-based
      // smoothing above and the whole-socket global smoothing below.
      main_rectification_sculpt_smooth: { id: 'main_rectification_sculpt_smooth', name: TOOL_NAMES.main_rectification_sculpt_smooth, icon: 'brush', kind: 'rectificationSculptSmooth', category: 'operation', group: 'smoothing', order: 7 },
      // Same shared card as Prepare Scan / Flares global smoothing (`kind: 'globalSmooth'`),
      // with its own id so applying and undoing here is tracked separately.
      main_rectification_global_smooth: { id: 'main_rectification_global_smooth', name: TOOL_NAMES.main_rectification_global_smooth, icon: 'wandSparkles', kind: 'globalSmooth', category: 'operation', group: 'smoothing', order: 8 },
      // Read-only measuring stack (original scan vs. the socket as it stands) —
      // `measurement` category, so it renders on the scene rail beside
      // Measure / Circumference rather than in the toolbar. Defined here because
      // it seeds from the applied circumference modification, and reused by the
      // later steps through their STEP_CONFIGS tool lists.
      main_rectification_circumference_lines: { id: 'main_rectification_circumference_lines', name: TOOL_NAMES.main_rectification_circumference_lines, icon: 'tableProperties', kind: 'circumferenceLines', category: 'measurement', order: 9 },
    },
    ELONGATION: {
      main_elongation_elongation: { id: 'main_elongation_elongation', name: 'Elongation', icon: 'maximize2', kind: 'elongation', category: 'operation', order: 1 },
    },
    TRIMLINE: {
      main_trimline_trimline: { id: 'main_trimline_trimline', name: 'Trimline', icon: 'penLine', kind: 'trimline', category: 'operation', order: 1 },
    },
    FLARES: {
      main_flares_flare: { id: 'main_flares_flare', name: 'Flare', icon: 'squareRoundCorner', kind: 'flare', category: 'operation', order: 1 },
      main_flares_smoothning: { id: 'main_flares_smoothning', name: 'Flares Smoothening', icon: 'sparkles', category: 'operation', order: 2 },
      // Same shared card as Prepare Scan's global smoothing (`kind: 'globalSmooth'`),
      // but its own id so applying/undoing here is tracked separately from the
      // scan-prep apply.
      main_flares_global_smooth: { id: 'main_flares_global_smooth', name: TOOL_NAMES.main_flares_global_smooth, icon: 'wandSparkles', kind: 'globalSmooth', category: 'operation', order: 3 },
    },
    ML_REDUCTION: {
      main_ml_reduction_brush: { id: 'main_ml_reduction_brush', name: 'ML reduction', icon: 'wandSparkles', kind: 'mlReduction', category: 'operation', order: 1 },
      main_ml_reduction_smoothning: { id: 'main_ml_reduction_smoothning', name: 'Smoothing', icon: 'sparkles', kind: 'mlSmooth', category: 'operation', order: 2 },
    },
    THICKNESS: {
      main_thickness_thickness: { id: 'main_thickness_thickness', name: 'Thickness', icon: 'slidersHorizontal', kind: 'thickness', category: 'operation', order: 1 },
      main_thickness_measure: { id: 'main_thickness_measure', name: 'Measure', icon: 'ruler', category: 'measurement', order: 2 },
      main_thickness_adjust: { id: 'main_thickness_adjust', name: 'Adjust', icon: 'slidersHorizontal', category: 'operation', order: 3 },
    },
    LOCKING_MECHANISM: {
      main_alignment_socket_pose: {
        id: 'main_alignment_socket_pose',
        name: TOOL_NAMES.main_alignment_socket_pose,
        icon: 'move',
        category: 'operation',
        order: 1,
      },
      main_locking_lock: { id: 'main_locking_lock', name: 'Locking System', icon: 'lock', category: 'operation', order: 2 },
    },
    VALVE: {
      main_valve_valve: { id: 'main_valve_valve', name: 'Valve', icon: 'wrench', kind: 'valve', category: 'operation', order: 1 },
      main_valve_position: { id: 'main_valve_position', name: 'Position', icon: 'locateFixed', category: 'operation', order: 2 },
      main_valve_adjust: { id: 'main_valve_adjust', name: 'Adjust', icon: 'slidersHorizontal', category: 'operation', order: 3 },
    },
    VALVE_PATTERN: {
      main_valve_pattern_projection: {
        id: 'main_valve_pattern_projection',
        name: TOOL_NAMES.main_valve_pattern_projection,
        icon: 'wandSparkles',
        category: 'operation',
        order: 1,
      },
    },
    LOGO_PLACEMENT: {
      main_logo_placement: {
        id: 'main_logo_placement',
        name: TOOL_NAMES.main_logo_placement,
        icon: 'image',
        kind: 'logo',
        category: 'operation',
        order: 1,
      },
    },
    PADS: {
      main_pads_pad: { id: 'main_pads_pad', name: 'Pad', icon: 'shield', category: 'operation', order: 1 },
      main_pads_position: { id: 'main_pads_position', name: 'Position', icon: 'locateFixed', category: 'operation', order: 2 },
      main_pads_adjust: { id: 'main_pads_adjust', name: 'Adjust', icon: 'slidersHorizontal', category: 'operation', order: 3 },
    },
    FINALIZATION: {
      main_finalization_review: { id: 'main_finalization_review', name: 'Review', icon: 'eye', category: 'operation', order: 1 },
      main_finalization_export: { id: 'main_finalization_export', name: 'Export', icon: 'upload', category: 'operation', order: 2 },
      main_finalization_save: { id: 'main_finalization_save', name: 'Save', icon: 'save', category: 'operation', order: 3 },
    },
  },
};

export const POINTER_TOOL_CONFIG = {
  id: 'pointer',
  name: TOOL_NAMES.pointer,
  icon: 'pointer',
  category: 'navigation',
};

export const TOOL_ICON_COMPONENTS = {
  pointer: MousePointer2,
  scissors: Scissors,
  lasso: LassoSelect,
  eraser: Eraser,
  paintBucket: PaintBucket,
  move: Move,
  rotateCw: RotateCw,
  ruler: Ruler,
  scaling: Scaling,
  circleDashed: CircleDashed,
  compass: Compass,
  slidersHorizontal: SlidersHorizontal,
  hand: Hand,
  sparkles: Sparkles,
  wandSparkles: WandSparkles,
  maximize2: Maximize2,
  penLine: PenLine,
  lock: Lock,
  locateFixed: LocateFixed,
  wrench: Wrench,
  shield: Shield,
  eye: Eye,
  upload: Upload,
  save: Save,
  circleDashed: CircleDashed,
  squareRoundCorner: SquareRoundCorner,
  image: Image,
  expand: Expand,
  brush: Brush,
  tableProperties: TableProperties,
};

/**
 * Workflow step id → the tool ids that live on that step.
 *
 * The step group keys inside {@link TOOLS_CONFIG} are the step ids themselves
 * (`RECTIFICATION`, `TRIMLINE`, …), so this map falls straight out of the config
 * rather than being maintained by hand.
 *
 * @type {Record<string, string[]>}
 */
export const TOOL_IDS_BY_STEP = Object.values(TOOLS_CONFIG).reduce((acc, workflow) => {
  Object.entries(workflow).forEach(([stepId, stepConfig]) => {
    acc[stepId] = Object.entries(stepConfig)
      .filter(([, toolConfig]) => !!toolConfig)
      .map(([toolKey, toolConfig]) => toolConfig.id || toolKey);
  });
  return acc;
}, {});

export const TOOL_CONFIG_BY_ID = Object.values(TOOLS_CONFIG).reduce((acc, workflow) => {
  Object.values(workflow).forEach((stepConfig) => {
    Object.entries(stepConfig).forEach(([toolKey, toolConfig]) => {
      if (!toolConfig) return;
      const resolvedToolId = toolConfig.id || toolKey;
      acc[resolvedToolId] = { ...toolConfig, id: resolvedToolId };
    });
  });
  return acc;
}, {});

/**
 * Tool category whose buttons live on the scene overlay (right rail, under the
 * info-panel gear) rather than in the left step toolbar.
 */
export const MEASUREMENT_TOOL_CATEGORY = 'measurement';

/** @param {string} toolId */
export const isMeasurementTool = (toolId) =>
  TOOL_CONFIG_BY_ID[toolId]?.category === MEASUREMENT_TOOL_CATEGORY;
